import type { ReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import axios from 'axios';

export interface SavedReport {
  id: number;
  name: string;
  /**
   * The saved view. Stored server-side as an opaque blob, so the shape is owned
   * here — adding a filter needs no API change.
   */
  config: { filters: Partial<ReportingV2Filters> };
  updatedAt?: string;
}

/**
 * Saved reports written before #4614 hold campaign / line-item NAMES under
 * `campaignIds` / `lineItemIds`. Names can't become ribeye ids, so they're
 * dropped on load; `clearedLegacy` says whether the report actually filtered on
 * them, so the menu can ask the user to reselect instead of silently widening.
 * The ribeye fields default to empty so loading an old report replaces a
 * campaign / line item picked before it, like every other filter it carries.
 */
export const migrateSavedFilters = (
  saved: Record<string, unknown>,
): { filters: Partial<ReportingV2Filters>; clearedLegacy: boolean } => {
  const { campaignIds, lineItemIds, ...rest } = saved;
  const nonEmpty = (value: unknown) => Array.isArray(value) && value.length > 0;
  return {
    filters: { ribeyeCampaignIds: [], ribeyeLineItemIds: [], ...rest } as Partial<ReportingV2Filters>,
    clearedLegacy: nonEmpty(campaignIds) || nonEmpty(lineItemIds),
  };
};

const PATH = '/reporting-2.0/saved-reports';

export const listSavedReports = async (): Promise<SavedReport[]> => {
  const response = await axios.get(PATH);
  return (response.data ?? []) as SavedReport[];
};

export const createSavedReport = async (name: string, filters: Partial<ReportingV2Filters>): Promise<SavedReport> => {
  const response = await axios.post(PATH, { name, config: { filters } });
  return response.data as SavedReport;
};

export const updateSavedReport = async (
  id: number,
  payload: { name?: string; filters?: Partial<ReportingV2Filters> },
): Promise<SavedReport> => {
  const response = await axios.patch(`${PATH}/${id}`, {
    ...(payload.name !== undefined ? { name: payload.name } : {}),
    ...(payload.filters !== undefined ? { config: { filters: payload.filters } } : {}),
  });
  return response.data as SavedReport;
};

export const deleteSavedReport = async (id: number): Promise<void> => {
  await axios.delete(`${PATH}/${id}`);
};
